// packages/api-client/src/services/public/publicAgentService.ts
import { getApiClient } from '../../../client';
import { handleApiError } from '../../../utils/handleApiError';
import type { ServiceResponse, AgentProfile, AgentProfileSummary } from '@dopomogai/types';

const PUBLIC_AGENTS_PREFIX = '/public-agents';

/**
 * Service for interacting with the public-facing agent endpoints.
 */
export const publicAgentService = {
  /**
   * Fetches a list of agent summaries for the public directory or homepage.
   * This is a lightweight call that only returns essential data for preview cards.
   *
   * @returns A promise that resolves with an array of agent summaries or an API error.
   */
  listAgentSummaries: (): ServiceResponse<AgentProfileSummary[]> => {
    return getApiClient().get<AgentProfileSummary[]>(`${PUBLIC_AGENTS_PREFIX}/`)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'listing public agent summaries'));
  },

  /**
   * Fetches the full, detailed profile for a single agent by its unique ID.
   * This is the primary data source for an agent's detail page.
   *
   * @param agentId The unique ID/slug of the agent (e.g., 'ecom-concierge-v1').
   * @returns A promise that resolves with the full AgentProfile or an API error.
   */
  getAgentProfile: (agentId: string): ServiceResponse<AgentProfile> => {
    return getApiClient().get<AgentProfile>(`${PUBLIC_AGENTS_PREFIX}/${agentId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching agent profile '${agentId}'`));
  },
};